import { IContentBlockInstance } from '@/models/content-page.model';
import { SupportedLocales } from '@/models/localization.model';
import { ContentBlockSchemaRoot } from '@/models/content-block-schema.model';
import { normalizeRootSchema } from './schemaDefaults';

export const isLocaleStringMap = (v: unknown, locales: SupportedLocales[]): v is Record<string, string> => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  const o = v as Record<string, unknown>;
  const keys = Object.keys(o);
  if (keys.length === 0) return false;
  return keys.every((k) => locales.includes(k as SupportedLocales) && typeof o[k] === 'string');
};

const isLocaleStringArrayMap = (v: unknown, locales: SupportedLocales[]): v is Record<string, string[]> => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  const o = v as Record<string, unknown>;
  const keys = Object.keys(o);
  if (keys.length === 0) return false;
  return keys.every(
    (k) =>
      locales.includes(k as SupportedLocales) &&
      Array.isArray(o[k]) &&
      (o[k] as unknown[]).every((x) => typeof x === 'string'),
  );
};

const LOCALE_AGNOSTIC_STRING_ARRAY_KEYS = new Set(['uuids', 'skus', 'category_ids']);

function pickSharedStringArray(vals: unknown[]): string[] {
  const nonEmpty = vals.find((v) => Array.isArray(v) && (v as unknown[]).length > 0);
  if (nonEmpty) return [...(nonEmpty as string[])];
  const any = vals.find((v) => Array.isArray(v));
  return any ? [...(any as string[])] : [];
}

/** String fields shared across locales (`translatable: false`), e.g. image URL, link, button color. */
const LOCALE_AGNOSTIC_STRING_KEYS = new Set(['image', 'link', 'color']);

function pickSharedString(vals: unknown[]): string {
  const nonEmpty = vals.find((v) => typeof v === 'string' && v.length > 0);
  if (nonEmpty !== undefined) return nonEmpty as string;
  const any = vals.find((v) => typeof v === 'string');
  return typeof any === 'string' ? any : '';
}

function mergeButtonObject(
  localeBlocks: Record<string, IContentBlockInstance[]>,
  blockIndex: number,
  locales: SupportedLocales[],
): Record<string, unknown> | null {
  const buttons = locales.map((loc) => {
    const d = localeBlocks[loc]?.[blockIndex]?.data as Record<string, unknown> | undefined;
    return d?.button as Record<string, unknown> | undefined;
  });
  if (!buttons.some(Boolean)) return null;

  const keySet = new Set<string>();
  buttons.forEach((b) => {
    if (b && typeof b === 'object') Object.keys(b).forEach((k) => keySet.add(k));
  });

  const out: Record<string, unknown> = {};
  keySet.forEach((bk) => {
    const vals = locales.map((_, idx) => buttons[idx]?.[bk]);

    if (LOCALE_AGNOSTIC_STRING_KEYS.has(bk)) {
      const nestedMap = vals.find((v) => isLocaleStringMap(v, locales));
      if (nestedMap) {
        const m = nestedMap as Record<string, string>;
        out[bk] = pickSharedString(locales.map((l) => m[l]));
      } else {
        out[bk] = pickSharedString(vals);
      }
      return;
    }

    const existingMap = vals.find((v) => isLocaleStringMap(v, locales));
    if (existingMap) {
      out[bk] = { ...existingMap };
      return;
    }

    if (vals.every((v) => v == null || typeof v === 'string')) {
      out[bk] = locales.reduce<Record<string, string>>((acc, loc, i) => {
        const s = vals[i];
        acc[loc] = typeof s === 'string' ? s : '';
        return acc;
      }, {} as Record<string, string>);
    } else {
      out[bk] = vals.find((v) => v != null);
    }
  });
  return out;
}

function splitButtonMerged(
  btn: Record<string, unknown>,
  locales: SupportedLocales[],
): Record<SupportedLocales, Record<string, unknown>> {
  const result = {} as Record<SupportedLocales, Record<string, unknown>>;
  locales.forEach((l) => {
    result[l] = {};
  });

  Object.keys(btn).forEach((bk) => {
    const bv = btn[bk];
    if (isLocaleStringMap(bv, locales)) {
      const m = bv as Record<string, string>;
      locales.forEach((l) => {
        result[l][bk] = m[l] ?? '';
      });
    } else {
      locales.forEach((l) => {
        result[l][bk] = bv;
      });
    }
  });

  return result;
}

function splitGalleryDataToLocalesInternal(
  mergedRows: unknown[],
  locales: SupportedLocales[],
): Record<SupportedLocales, unknown[]> {
  const out = {} as Record<SupportedLocales, unknown[]>;
  locales.forEach((l) => {
    out[l] = mergedRows.map((row) => {
      if (!row || typeof row !== 'object' || Array.isArray(row)) return row;
      const o = row as Record<string, unknown>;
      const rowOut: Record<string, unknown> = {};
      Object.keys(o).forEach((key) => {
        const v = o[key];
        if (isLocaleStringMap(v, locales)) {
          const m = v as Record<string, string>;
          rowOut[key] = m[l] ?? '';
        } else {
          rowOut[key] = v;
        }
      });
      return rowOut;
    });
  });
  return out;
}

function mergeGalleryItemsField(vals: unknown[], locales: SupportedLocales[]): unknown[] {
  const perLocaleRows = vals.map((v) => (Array.isArray(v) ? v : []));
  const maxLen = Math.max(0, ...perLocaleRows.map((rows) => rows.length));
  const mergedRows: unknown[] = [];

  for (let i = 0; i < maxLen; i++) {
    const rowVals = perLocaleRows.map((rows) =>
      rows[i] && typeof rows[i] === 'object' && !Array.isArray(rows[i]) ? (rows[i] as Record<string, unknown>) : {},
    );
    const keySet = new Set<string>();
    rowVals.forEach((r) => Object.keys(r).forEach((k) => keySet.add(k)));
    const rowOut: Record<string, unknown> = {};

    keySet.forEach((key) => {
      const keyVals = rowVals.map((row) => row[key]);
      if (LOCALE_AGNOSTIC_STRING_KEYS.has(key)) {
        rowOut[key] = pickSharedString(keyVals);
        return;
      }
      const existingMap = keyVals.find((v) => isLocaleStringMap(v, locales));
      if (existingMap) {
        rowOut[key] = { ...(existingMap as Record<string, string>) };
        return;
      }
      if (keyVals.every((v) => v == null || typeof v === 'string')) {
        rowOut[key] = locales.reduce<Record<string, string>>((acc, locale, idx) => {
          const raw = keyVals[idx];
          acc[locale] = typeof raw === 'string' ? raw : '';
          return acc;
        }, {});
        return;
      }
      rowOut[key] = keyVals.find((v) => v != null);
    });

    mergedRows.push(rowOut);
  }

  return mergedRows;
}

/** Merge `locale.*.content[i].data` from parallel rows into one object for the inspector (UK+RU tabs). */
export function mergeBlockDataForInspector(
  blockIndex: number,
  localeBlocks: Record<string, IContentBlockInstance[]>,
  locales: SupportedLocales[],
): Record<string, unknown> | null {
  const rows = locales.map((l) => localeBlocks[l]?.[blockIndex]);
  if (!rows.some(Boolean)) return null;

  const keySet = new Set<string>();
  rows.forEach((b) => {
    const d = b?.data;
    if (d && typeof d === 'object' && !Array.isArray(d)) {
      Object.keys(d as object).forEach((k) => keySet.add(k));
    }
  });

  const out: Record<string, unknown> = {};

  keySet.forEach((key) => {
    const vals = locales.map((loc) => {
      const d = localeBlocks[loc]?.[blockIndex]?.data as Record<string, unknown> | undefined;
      return d?.[key];
    });

    if (LOCALE_AGNOSTIC_STRING_KEYS.has(key)) {
      const nestedMap = vals.find((v) => isLocaleStringMap(v, locales));
      if (nestedMap) {
        const m = nestedMap as Record<string, string>;
        out[key] = pickSharedString(locales.map((l) => m[l]));
      } else {
        out[key] = pickSharedString(vals);
      }
      return;
    }

    const existingMap = vals.find((v) => isLocaleStringMap(v, locales));
    if (existingMap) {
      out[key] = { ...existingMap };
      return;
    }

    if (LOCALE_AGNOSTIC_STRING_ARRAY_KEYS.has(key)) {
      const nestedMap = vals.find((v) => isLocaleStringArrayMap(v, locales));
      if (nestedMap) {
        const m = nestedMap as Record<string, string[]>;
        out[key] = pickSharedStringArray(locales.map((l) => m[l]));
      } else {
        out[key] = pickSharedStringArray(vals);
      }
      return;
    }

    const existingArrMap = vals.find((v) => isLocaleStringArrayMap(v, locales));
    if (existingArrMap) {
      out[key] = { ...existingArrMap };
      return;
    }

    if (vals.every((v) => v == null || (Array.isArray(v) && v.every((x) => typeof x === 'string')))) {
      out[key] = locales.reduce<Record<string, string[]>>((acc, loc, i) => {
        const v = vals[i];
        acc[loc] = Array.isArray(v) ? [...v] : [];
        return acc;
      }, {} as Record<string, string[]>);
      return;
    }

    if (vals.every((v) => v == null || typeof v === 'string')) {
      out[key] = locales.reduce<Record<string, string>>((acc, loc, i) => {
        const s = vals[i];
        acc[loc] = typeof s === 'string' ? s : '';
        return acc;
      }, {} as Record<string, string>);
      return;
    }

    if (key === 'button') {
      const mergedBtn = mergeButtonObject(localeBlocks, blockIndex, locales);
      if (mergedBtn && Object.keys(mergedBtn).length > 0) {
        out[key] = mergedBtn;
        return;
      }
    }

    if (key === 'items' && vals.some((v) => Array.isArray(v))) {
      out[key] = mergeGalleryItemsField(vals, locales);
      return;
    }

    out[key] = vals.find((v) => v != null);
  });

  return out;
}

/** Split inspector `data` back into per-locale plain objects for API rows. */
export function splitMergedBlockDataToLocales(
  merged: Record<string, unknown>,
  locales: SupportedLocales[],
): Record<SupportedLocales, Record<string, unknown>> {
  const out = {} as Record<SupportedLocales, Record<string, unknown>>;
  locales.forEach((l) => {
    out[l] = {};
  });

  Object.keys(merged).forEach((key) => {
    const val = merged[key];

    if (isLocaleStringMap(val, locales)) {
      const m = val as Record<string, string>;
      locales.forEach((l) => {
        out[l][key] = m[l] ?? '';
      });
      return;
    }

    if (isLocaleStringArrayMap(val, locales)) {
      const m = val as Record<string, string[]>;
      locales.forEach((l) => {
        out[l][key] = m[l] ? [...m[l]] : [];
      });
      return;
    }

    if (key === 'button' && val && typeof val === 'object' && !Array.isArray(val)) {
      const perBtn = splitButtonMerged(val as Record<string, unknown>, locales);
      locales.forEach((l) => {
        out[l][key] = perBtn[l];
      });
      return;
    }

    if (key === 'items' && Array.isArray(val)) {
      const perLocaleItems = splitGalleryDataToLocalesInternal(val, locales);
      locales.forEach((l) => {
        out[l][key] = perLocaleItems[l];
      });
      return;
    }

    locales.forEach((l) => {
      out[l][key] = val;
    });
  });

  return out;
}

export function mergeGalleryDataForInspector(
  blockIndex: number,
  localeBlocks: Record<string, IContentBlockInstance[]>,
  locales: SupportedLocales[],
): unknown[] | null {
  const lengths = locales.map((l) => {
    const d = localeBlocks[l]?.[blockIndex]?.data;
    return Array.isArray(d) ? d.length : 0;
  });
  const maxLen = Math.max(0, ...lengths);
  if (maxLen === 0) return null;

  const merged: unknown[] = [];
  for (let i = 0; i < maxLen; i++) {
    const rowVals = locales.map((loc) => {
      const d = localeBlocks[loc]?.[blockIndex]?.data as unknown[] | undefined;
      return d?.[i] as Record<string, unknown> | undefined;
    });

    const keySet = new Set<string>();
    rowVals.forEach((r) => {
      if (r && typeof r === 'object') Object.keys(r).forEach((k) => keySet.add(k));
    });

    const rowOut: Record<string, unknown> = {};
    keySet.forEach((key) => {
      const vals = locales.map((_, idx) => rowVals[idx]?.[key]);

      if (LOCALE_AGNOSTIC_STRING_KEYS.has(key)) {
        const nestedMap = vals.find((v) => isLocaleStringMap(v, locales));
        if (nestedMap) {
          const m = nestedMap as Record<string, string>;
          rowOut[key] = pickSharedString(locales.map((l) => m[l]));
        } else {
          rowOut[key] = pickSharedString(vals);
        }
        return;
      }

      const existingMap = vals.find((v) => isLocaleStringMap(v, locales));
      if (existingMap) {
        rowOut[key] = { ...existingMap };
        return;
      }

      if (vals.every((v) => v == null || typeof v === 'string')) {
        rowOut[key] = locales.reduce<Record<string, string>>((acc, loc, idx) => {
          const s = vals[idx];
          acc[loc] = typeof s === 'string' ? s : '';
          return acc;
        }, {} as Record<string, string>);
      } else {
        rowOut[key] = vals.find((v) => v != null);
      }
    });
    merged.push(rowOut);
  }
  return merged;
}

export function splitGalleryDataToLocales(
  mergedRows: unknown[],
  locales: SupportedLocales[],
): Record<SupportedLocales, unknown[]> {
  return splitGalleryDataToLocalesInternal(mergedRows, locales);
}

export function getInspectorBlock(
  block: IContentBlockInstance,
  blockIndex: number,
  localeBlocks: Record<string, IContentBlockInstance[]>,
  locales: SupportedLocales[],
  schema: ContentBlockSchemaRoot | undefined,
): IContentBlockInstance {
  if (!schema) return block;
  const root = normalizeRootSchema(schema);

  if (root.type === 'array') {
    const merged = mergeGalleryDataForInspector(blockIndex, localeBlocks, locales);
    if (merged) return { ...block, data: merged };
    return block;
  }

  const merged = mergeBlockDataForInspector(blockIndex, localeBlocks, locales);
  if (merged) return { ...block, data: merged };
  return block;
}
